import {
  Button,
  DebouncedInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { IconSearch } from "@tabler/icons-react";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { BaseUser } from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getImpersonationDetails, getUserList } from "@/helpers/apis";
import { impersonateUser } from "@/services/auth";
import { GET_USER_LIST } from "@/services/constants";
import { FC, useState } from "react";
import { getTenant } from "@/helpers/tenant";

interface ImpersonationDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ImpersonationDialog: FC<ImpersonationDialogProps> = ({ open, onOpenChange }) => {
  const [userSearchString, setUserSearchString] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const tenant = getTenant();

  const userTableHeaders = [
    {
      id: 1,
      label: "Name",
      uid: "name",
      width: 75,
      render: (item: BaseUser) => (
        <span>
          {item.first_name} {item.last_name}
        </span>
      ),
    },
    {
      id: 2,
      label: "Email",
      uid: "email",
      width: 75,
      render: (item: BaseUser) => <span>{item.email}</span>,
    },
    {
      id: 3,
      label: "Role",
      uid: "role",
      width: 15,
      render: (item: BaseUser) => <span>{item.role_title}</span>,
    },
    {
      id: 4,
      label: "",
      uid: "action",
      width: 15,
      render: (item: BaseUser) => (
        <Button
          disabled={isImpersonationLoading}
          variant="secondary"
          size="xs"
          onClick={() => getImpersonationUser({ email: item.email })}
        >
          Impersonate
        </Button>
      ),
    },
  ];

  const { mutate: getImpersonationUser, isPending: isImpersonationLoading } = useMutation({
    mutationKey: ["impersonateUser"],
    mutationFn: (data: { email: string }) => getImpersonationDetails(data),
    onSuccess: data => {
      if (data) {
        impersonateUser(data);
      }
      onOpenChange?.(false);
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to impersonate user",
        description: err.message,
      });
    },
  });

  const { data: users, isPending: isUsersListLoading } = useQuery({
    queryKey: [GET_USER_LIST, tenant, page, pageSize, userSearchString],
    queryFn: () =>
      getUserList({
        company: tenant,
        page,
        pageSize,
        with_service_user: false,
        include_deleted: false,
        search_term: userSearchString,
      }),
    enabled: open,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={"!al-max-w-[65vw]"}>
        <DialogHeader>
          <DialogTitle>Impersonate User</DialogTitle>
          <DialogDescription>
            Select the user you want to impersonate, this will allow you to view the application as
            that user.
          </DialogDescription>
        </DialogHeader>
        <Stack
          direction={"column"}
          gap={"lg"}
          className={"al-max-h-[80vh] al-overflow-auto al-p-1"}
        >
          <DebouncedInput
            value={userSearchString}
            debounceMs={1000}
            icon={IconSearch}
            placeholder={"Search by email or name"}
            className={"al-w-full"}
            onChange={setUserSearchString}
          />
          {isUsersListLoading ? (
            <Stack className={"al-min-h-32"} justify={"center"} align={"center"} gap={"lg"}>
              <Spinner />
              <span>Loading users</span>
            </Stack>
          ) : (
            <>
              <DataTable
                header={userTableHeaders}
                onRowClick={() => {}}
                data={{
                  items: users?.users ?? [],
                }}
                resetPage={() => {}}
              />
              <Paginate
                showPageSize
                itemCount={users?.meta?.count ?? 0}
                page={page}
                numPages={users?.meta?.total_pages ?? 0}
                onPageClick={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
